 
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedBrandPath, getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import truncate from "lodash/truncate";
import * as React from "react";
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface NISCategoryDescriptionStyles {
    CategoryDetails?: InjectableCss;
    linkStyles?: InjectableCss;
}

export const nisCategoryDescriptionStyles: NISCategoryDescriptionStyles = {
    CategoryDetails: {
        css: css`
            p {
                font-size: 18px !important;
                font-weight: ${TYPOGRAPHY.weight.base};
                line-height: 28px;
                font-family: "Poppins" !important;
                color: ${getProp("theme.typography.p.color")} !important;
            }

            html body {
                font-size: 16px !important;
                font-family: "Poppins" !important;
                p {
                    margin-top: 0px;
                    margin-bottom: 0.5rem !important;
                    font-size: 18px !important;
                    font-weight: ${TYPOGRAPHY.weight.base};
                    line-height: 28px;
                    font-family: "Poppins" !important;
                    color: ${getProp("theme.typography.p.color")} !important;
                }
            }
        `,
    },
    linkStyles: {
        css: css`
            margin-top: 0px;
            margin-bottom: 10px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 0px;
                    `,
                    css`
                        margin-bottom: 0px;
                    `,
                    css`
                        margin-bottom: 0px;
                    `,
                    null,
                    null,
                ])}
            &:focus {
                outline-color: transparent !important;
            }
            > span {
                color: ${getProp("theme.typography.p.color")} !important ;
                font-size: ${TYPOGRAPHY.size.large};
                font-weight: ${TYPOGRAPHY.weight.mediumbold};
                line-height: ${TYPOGRAPHY.lineHeight.base};
            }
        `,
    },
};

const styles = nisCategoryDescriptionStyles;

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const categoryPath =
        getSelectedCategoryPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const catalogPageState = getCatalogPageStateByPath(state, categoryPath);
    const { pathname } = getLocation(state);
    const brandPath = getSelectedBrandPath(state) || pathname;
    return {
        currentPage: getCurrentPage(state),
        catalogPageState,
        breadCrumbs: state.components.breadcrumbs.links,
        categoryFacets: getProductListDataViewProperty(state, "properties"),
        brandPath,
    };
};

type Props = WidgetProps & HasCategoryContext & ReturnType<typeof mapStateToProps>;

const NISCategoryDescription = ({ category, catalogPageState, brandPath, categoryFacets, breadCrumbs }: Props) => {
    const subCategories =
        catalogPageState?.value?.properties?.subCategories &&
        JSON.parse(decodeURI(catalogPageState?.value.properties?.subCategories));

    const isBrandPath = brandPath?.toLowerCase()?.includes("/brands");
    const categoryFacetsData = JSON.parse(categoryFacets?.categoryList ?? "[]");

    const lastLevelCategory = breadCrumbs?.[breadCrumbs?.length - 1]?.children;

    const categoryFacetsDataNew = categoryFacetsData?.filter((item: any) => item.Name !== lastLevelCategory);

    if (
        !category ||
        !category.htmlContent ||
        (categoryFacetsDataNew?.length === 0 && isBrandPath) ||
        (subCategories?.length === 0 && !isBrandPath) ||
        !subCategories
    ) {
        return null;
    }

    const [status, setStatus] = useState(false);
    const [truncateLength, setTruncateLength] = useState(220);

    const toggleStatus = () => {
        setStatus(!status);
    };

    const contentArr = category.htmlContent.split("</head>");
    const contentLength = contentArr?.length > 1 ? contentArr[1].length : category.htmlContent?.length;

    useEffect(() => {
        if (typeof window === "undefined") { return; }
        const handleResize = () => {
            setTruncateLength(window.innerWidth <= 768 ? 135 : 220);
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return (
        <StyledWrapper {...styles.CategoryDetails}>
            {status
                ? parse(transformContentHtml(category.htmlContent), parserOptions)
                : parse(truncate(transformContentHtml(category.htmlContent), { length: truncateLength }), parserOptions)}
            {contentLength > truncateLength && (
                <Link href="" onClick={toggleStatus} {...styles.linkStyles}>
                    {status ? "See Less" : "See More"}
                </Link>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withCategory(NISCategoryDescription)),
    definition: {
        group: "Categories",
        icon: "folder-bookmark",
        allowedContexts: ["CategoryDetailsPage", "ProductDetailsPage", "ProductListPage"],
    },
};

export default widgetModule;
